import React from "react";
import { Eye } from "lucide-react";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { formatProductTypeDomainName } from "@/lib/formatProductType";
import { formatCanonicalCategoryLeaf } from "@/lib/formatCanonicalCategory";
import { MappingStatusBadge } from "../MappingStatusBadge";
import { ConfidenceBadge } from "../ConfidenceBadge";
import { marketplaceLogo } from "@/lib/marketplaceLogo";
import { rowBase, selectedRow } from "./MappingTableRow.styles";
import type { MappingTableRowProps } from "./MappingTableRow.types";

function formatMappedDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export const MappingTableRow: React.FC<MappingTableRowProps> = ({
  mapping,
  isSelected = false,
  onSelect,
}) => {
  const logo = marketplaceLogo(mapping.marketplace);
  const productTypeDomainName =
    formatProductTypeDomainName(mapping.productType) ||
    formatProductTypeDomainName(mapping.displayName) ||
    mapping.displayName;
  const canonicalCategoryLeaf = formatCanonicalCategoryLeaf(
    mapping.canonicalCategory,
  );

  return (
    <tr
      data-testid={`mapping-row-${mapping.id}`}
      className={cn(rowBase, isSelected && selectedRow)}
      onClick={() => onSelect(mapping)}
      aria-selected={isSelected}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {logo ? (
            <img
              src={logo}
              alt={mapping.marketplace}
              className="h-10 w-10 object-contain"
            />
          ) : (
            <span className="h-10 w-10 rounded bg-slate-200 dark:bg-slate-700" />
          )}
          <p
            className="text-sm font-medium text-slate-900 dark:text-slate-100"
            title={mapping.productType || mapping.displayName}
          >
            {productTypeDomainName}
          </p>
        </div>
      </td>
      <td className="px-4 py-3">
        {canonicalCategoryLeaf ? (
          <span
            className="text-sm font-medium text-indigo-600 dark:text-indigo-400"
            title={mapping.canonicalCategory ?? canonicalCategoryLeaf}
          >
            {canonicalCategoryLeaf}
          </span>
        ) : (
          <span className="text-sm italic text-slate-400 dark:text-slate-500">
            Not mapped
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <MappingStatusBadge status={mapping.status} />
      </td>
      <td className="px-4 py-3">
        <ConfidenceBadge confidence={mapping.confidence} />
      </td>
      <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
        {formatMappedDate(mapping.mappedAt)}
      </td>
      <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
        {mapping.mappedBy ?? "—"}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={(event) => {
              event.stopPropagation();
              onSelect(mapping);
            }}
            title="View details"
            aria-label="View mapping session details"
          >
            <Eye className="w-3.5 h-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
};
